/**
 * Goal Auto-Card — for goal_announced and goal_achieved posts.
 * Clean minimalist card with goal info and progress.
 */

import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Target, PartyPopper } from 'lucide-react'

interface GoalAutoCardProps {
  postType: 'goal_announced' | 'goal_achieved'
  metadata: {
    goal_name?: unknown
    goal_category?: unknown
    goal_icon?: unknown
    days_taken?: unknown
  }
}

export const GoalAutoCard = memo(function GoalAutoCard({
  postType, metadata,
}: GoalAutoCardProps): React.ReactElement {
  const { t } = useTranslation()
  const isAchieved = postType === 'goal_achieved'
  const name = String(metadata.goal_name ?? t('feed.auto.unknownGoal'))
  const icon = String(metadata.goal_icon ?? '')
  const daysTaken = Number(metadata.days_taken ?? 0)

  const accentColor = isAchieved ? 'success' : 'primary'

  return (
    <div className={`rounded-2xl border p-4 ${
      isAchieved
        ? 'bg-gradient-to-br from-success/[0.06] to-emerald-500/[0.03] border-success/15'
        : 'bg-gradient-to-br from-primary/[0.06] to-indigo-500/[0.03] border-primary/15'
    }`}>
      {/* Top row */}
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
          isAchieved ? 'bg-success/15' : 'bg-primary/15'
        }`}>
          {isAchieved
            ? <PartyPopper size={17} className="text-success" />
            : <Target size={17} className="text-primary" />}
        </div>
        <span className={`text-[11px] font-semibold uppercase tracking-wider text-${accentColor}/70`}>
          {isAchieved ? t('feed.auto.goalAchieved') : t('feed.auto.goalAnnounced')}
        </span>
      </div>

      {/* Goal name */}
      <p className="text-[15px] font-semibold text-text leading-snug">
        {icon && <span className="mr-1.5">{icon}</span>}
        {name}
      </p>

      {/* Footer */}
      {isAchieved && daysTaken > 0 && (
        <p className="text-[11px] text-success/60 mt-2 font-medium">
          {t('feed.auto.achievedIn', { days: daysTaken })}
        </p>
      )}

      {!isAchieved && (
        <div className="mt-3">
          <div className="h-1 rounded-full bg-white/[0.06] overflow-hidden">
            <div className="h-full w-[3%] rounded-full bg-primary/60" />
          </div>
          <p className="text-[10px] text-primary/50 mt-1.5 font-medium">
            {t('feed.auto.started')}
          </p>
        </div>
      )}
    </div>
  )
})
